import { LazyMotion, domAnimation, m } from "framer-motion";
import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";
import HomeBannerVideo from "../../../components/Cards/BannerVideo/HomeVideo";
import CityNavigation from "../../../components/Cards/CityNavigation";
import CoreValues from "../../../components/Cards/CoreValues";
import FeaturedBanners from "../../../components/Cards/FeaturedBanners";
import LogoCarousel from "../../../components/Cards/LogoCarousel";
import CityTourCard from "../../../components/Cards/ProductCard";
import {
  addApolloState,
  initializeApollo,
} from "../../../graphql/apolloClient";
import {
  GetAllCountriesDocument,
  Iso639,
  ProductsResult,
} from "../../../graphql/operations";
import styles from "../../../styles/Home.module.scss";
import { addDuration, formatDate } from "../../../utils/calendarUtils";
import { GET_LOCATION_PRODUCTS_BY_SLUG } from "../../../utils/queries/ProductsByCity";
import Custom404 from "../../404";

interface CountryToursProps {
  result: {
    data: {
      products: ProductsResult;
    };
  };
}

const CountryToursPage = ({ result }: CountryToursProps) => {
  if (!result || result.data.products.totalRecords === 0) return <Custom404 />;
  const { products } = result.data;
  const { location } = products.records[0];
  const { country } = location;

  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2,
      },
    },
  };

  const item = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    },
  };

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, shrink-to-fit=no"
        />
        <meta name="theme-color" content="#de2c24" />
        <meta
          name="description"
          content={`${
            country.name
          } Small Group Tours with Local Guides. Book top food tours, night tours, and walking tours in ${
            country.name === "Thailand"
              ? "Bangkok, Chiang Mai, Phuket, Pattaya and Krabi Town."
              : country.name
          }.`}
        />
        <meta
          name="keywords"
          content={
            country.name === "Thailand"
              ? "thailand tours, thai tours, things to do in thailand, what to do in thailand, local guide tours in thailand, thailand tour guides"
              : "travel plan, itinerary, things to do, book, guide, trip, tours, experiences, vacation, reviews, japan tours"
          }
        />
        <meta property="og:url" content="https://www.magical-trip.com/" />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={`MagicalTrip: ${country.name}'s Best Local Tours by Local Guides`}
        />
        <meta
          property="og:description"
          content={`${
            country.name
          } Small Group Tours with Local Guides. Book top food tours, night tours, and walking tours in  ${
            country.name === "Thailand"
              ? "Bangkok, Chiang Mai, Phuket, Pattaya and Krabi Town."
              : country.name
          }.`}
        />
        <meta
          property="og:image"
          content="https://res.cloudinary.com/dbm1qiew0/image/upload/f_auto,q_auto/v1672881664/recruit/featured/mt-logo.jpg"
        />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@magicaltripcom" />
        <meta
          name="twitter:title"
          content={`MagicalTrip: ${country.name}'s Best Local Tours by Local Guides`}
        />
        <meta
          name="twitter:description"
          content={`${
            country.name
          } Small Group Tours with Local Guides. Book top food tours, night tours, and walking tours in  ${
            country.name === "Thailand"
              ? "Bangkok, Chiang Mai, Phuket, Pattaya and Krabi Town."
              : country.name
          }.`}
        />
        <meta
          name="twitter:image"
          content="https://res.cloudinary.com/dbm1qiew0/image/upload/f_auto,q_auto/v1672881664/recruit/featured/mt-logo.jpg"
        />
        <title>
          {`MagicalTrip: ${country.name}'s Best Local Tours by Local Guides`}
        </title>
      </Head>
      <HomeBannerVideo
        title={"Travel deeper \n with a Local Guide"}
        subtitle={`Book ${country.name}'s authentic small group tours by locals`}
        videoSrc={
          country.name === "Thailand"
            ? "https://res.cloudinary.com/dbm1qiew0/video/upload/f_auto,q_auto,h_300,dpr_2/v1737593638/s3bucket/ace-content/bqpvheh3a3a29s6hwfvr"
            : `https://res.cloudinary.com/dbm1qiew0/video/upload/f_auto,q_auto,h_300,dpr_2/v1724807983/s3bucket/ace-content/top-video`
        }
        coverPoster={
          country.name === "Thailand"
            ? "https://res.cloudinary.com/dbm1qiew0/image/upload/f_auto,q_auto,w_1024,h_460,c_fill/v1737009035/staging/products/f882f7f6-4213-41c4-8804-779452f46b54/images/15f9dd85-d4cf-44a2-9ab1-09a34a903dde.jpg"
            : "https://res.cloudinary.com/dbm1qiew0/image/upload/f_auto,q_auto,w_1024,h_460,c_fill/v1589163651/s3bucket/ace-content/DSC05116.jpg"
        }
      />

      <div className="AppLayout" style={{ marginTop: 40 }}>
        <h2 style={{ marginBottom: 15 }}>Find Local Tours in {country.name}</h2>
        <CityNavigation countryId={country.id} />
      </div>

      <div className="AppLayout">
        <h2 className="UIT UITSLL UITW1 UITC0 HomeCityHeader">
          <span>Tours in {country.name}</span>
        </h2>
        <div className={styles.CountryProducts}>
          {result.data.products.records.map((product, index) => {
            return <CityTourCard {...product} key={index} />;
          })}
        </div>
        <LazyMotion features={domAnimation}>
          <m.div
            variants={container}
            exit={{ opacity: 0 }}
            initial={"hidden"}
            animate={{ opacity: 1 }}
            whileInView="visible"
          >
            <m.div variants={item} style={{ marginTop: 65 }}>
              <CoreValues />
            </m.div>
          </m.div>
        </LazyMotion>
      </div>

      <FeaturedBanners
        showHome={true}
        locationName={country.name}
        locationId={country.id}
      />
      <div className="AppLayout">
        <LogoCarousel />
      </div>
    </>
  );
};

export default CountryToursPage;

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const apolloClient = initializeApollo();
    const result = await apolloClient.query({
      query: GetAllCountriesDocument,
    });
    const paths = result.data.locations.records.map((r) => ({
      params: { country: r.country.name.toLowerCase() },
    }));

    return { paths, fallback: "blocking" };
  } catch {
    return { paths: [], fallback: "blocking" };
  }
};

const revalidate = process.env.ENVIRONMENT == "production" ? 3600 : 60;

export const getStaticProps: GetStaticProps = async (context: {
  params: any;
  query: any;
  req: any;
}) => {
  const today = new Date();
  const until = addDuration(today, { days: 2 });
  let queryInput;
  let queryVar;

  queryInput = GET_LOCATION_PRODUCTS_BY_SLUG;
  queryVar = {
    locationSlug: context.params.country,
    language: Iso639.En,
    currency: "USD",
    fromDate: formatDate(today),
    untilDate: formatDate(until),
  };
  try {
    const apolloClient = initializeApollo();

    const result = await apolloClient.query({
      query: queryInput,
      variables: queryVar,
    });

    return addApolloState(apolloClient, {
      props: {
        result,
      },
      revalidate: revalidate,
    });
  } catch (error) {
    console.error("HERE", error);

    const result =
      error.code === "INVALID_UUID" ? { data: { products: null } } : null;

    return {
      props: {
        result,
      },
      revalidate: 1,
    };
  }
};
